import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { AppState } from '../../../../../store';
import { useTranslation } from 'react-i18next';
import { pushEvent } from '../../../../../utils/clevertapUtils';
import { useButtonStyles } from '../../../components/styles';
import { signSetAction } from '../../../../../store/signup/actions';
import { Country, LocationSelected } from '../../../../../store/signup/types';
import {
    CLICK_ORG_NAME_CITY_INPUT,
    CLICK_ORG_NAME_SEARCH_BTN,
    CLICK_ORG_NAME_ZIP_INPUT,
    VIEW_ORG_NAME_PAGE,
} from 'utils/clevertapEvents';
import { GivelifyForm, GivelifyFormTextField } from '@givelify/ui';
import * as Yup from 'yup';
import { useFormContext } from 'react-hook-form';
import { SuggestionsList } from 'pages/signup/components/SuggestionsList/SuggestionsList';
import { Features } from 'store/featureFlags/types';
import { useV2Translation } from './translation';
import { OrganizationName, StyledButton, Wrapper } from './styles';
import { LIST_SCROLL } from 'pages/signup/utils/useScrollPosition';
import { LocationAutoComplete } from 'pages/signup/components/LocationAutocomplete/LocationAutocomplete';
import {
    SEARCH_EXPERIMENT,
    SEARCH_UI_EXPERIMENT,
} from 'utils/clevertapVariantFlags';

const OrganizationAutoComplete: React.FC = () => {
    const { t } = useTranslation();
    const { submit, mainButton } = useButtonStyles();
    const copy = useV2Translation();
    const { newSearchUIEnabled, v2SearchAPIEnabled } = useSelector(
        (state: AppState) => ({
            newSearchUIEnabled:
                state.FeatureFlagging.features[Features.SEARCH_UI_V2],
            v2SearchAPIEnabled:
                state.FeatureFlagging.features[Features.SEARCH_API_V2],
        }),
    );

    const { nextLabelText } = useMemo(
        () => ({
            nextLabelText: t('labels.search'),
        }),
        [t],
    );

    const {
        formState: { isValid },
        watch,
        setValue,
        register,
    } = useFormContext<FormProps>();

    register('country');

    const country = watch('country');
    const setCountry = (value: Country) => {
        setValue('country', value);
    };

    return (
        <Wrapper className="fs-unmask" showZip={country === 'us'}>
            <OrganizationName>
                <GivelifyFormTextField<FormProps>
                    placeholder={copy.fullNameLabel}
                    label={copy.fullNameLabel}
                    fullWidth
                    size="large"
                    name="organizationName"
                    maxLength={255}
                />
            </OrganizationName>
            <LocationAutoComplete<FormProps>
                name="location"
                onClick={() => pushEvent(CLICK_ORG_NAME_CITY_INPUT)}
                country={country}
                setCountry={setCountry}
                style={{
                    gridArea: 'city',
                }}
            />
            {country === 'us' && (
                <GivelifyFormTextField<FormProps>
                    fullWidth
                    label={copy.zipCodeLabel}
                    placeholder={copy.zipCodeLabel}
                    size="large"
                    style={{
                        gridArea: 'zip',
                    }}
                    name="zip"
                    maxLength={5}
                    onClick={() =>
                        pushEvent(CLICK_ORG_NAME_ZIP_INPUT, {
                            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled
                                ? 'on'
                                : 'off',
                            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled
                                ? 'on'
                                : 'off',
                        })
                    }
                />
            )}
            <StyledButton
                className={`${submit} ${mainButton}`}
                variant="primary"
                type="submit"
                disabled={!isValid}
                text={nextLabelText}
                name="search"
            />
        </Wrapper>
    );
};

type FormProps = {
    organizationName: string;
    country: Country;
    location: LocationSelected;
    zip: string;
};

const getValidationSchema = (locationErrorMessage: string) =>
    Yup.object<FormProps>({
        organizationName: Yup.string()
            .transform((val, originalValue) =>
                typeof originalValue === 'string'
                    ? originalValue.replace(/[‘’‛]/g, "'").replace(/[“”]/g, '"')
                    : val,
            )
            .required('')
            .max(255),
        country: Yup.mixed<Country>().required().oneOf(['us', 'bs']),
        location: Yup.mixed<LocationSelected>().test(
            'validateLocation',
            locationErrorMessage,
            (val) => {
                const res = val as LocationSelected;
                return !!res.city && !!res.state;
            },
        ),
        zip: Yup.string().max(5),
    });

const Outer = () => {
    const dispatch = useAppDispatch();

    const [suggestionsOpen, setSuggestionsOpen] = useState<boolean>(false);

    const {
        defaultValueLoc,
        defaultValueOrgName,
        defaultCountry,
        newSearchUIEnabled,
        v2SearchAPIEnabled,
    } = useSelector((state: AppState) => ({
        defaultValueLoc: state.Signup.organizationLocation,
        defaultValueOrgName: state.Signup.organizationName,
        defaultCountry: state.Signup.organizationCountry,
        newSearchUIEnabled:
            state.FeatureFlagging.features[Features.SEARCH_UI_V2],
        v2SearchAPIEnabled:
            state.FeatureFlagging.features[Features.SEARCH_API_V2],
    }));

    const { t } = useTranslation();
    const { locationErrorMessage } = useMemo(
        () => ({
            locationErrorMessage: t(
                'signup.organizationSearch.locationErrorMessage',
            ),
        }),
        [t],
    );

    useEffect(() => {
        pushEvent(VIEW_ORG_NAME_PAGE, {
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: 'on',
        });

        if (defaultValueOrgName && defaultValueLoc) {
            setSuggestionsOpen(true);
        }
        // eslint-disable-next-line
    }, []);

    const onSubmit = (data: FormProps) => {
        const { country, location, organizationName, zip } = data;
        pushEvent(CLICK_ORG_NAME_SEARCH_BTN, { organizationName });

        const payload = {
            organizationName,
            organizationLocation: {
                ...location,
                zip: zip || location.zip,
            },
            organizationCountry: country,
        };

        dispatch(signSetAction(payload));
        setSuggestionsOpen(true);
        sessionStorage.setItem('organizationSearch', JSON.stringify(payload));
        sessionStorage.removeItem(LIST_SCROLL);
    };

    const formSchema = useMemo(
        () => getValidationSchema(locationErrorMessage),
        [locationErrorMessage],
    );

    return (
        <GivelifyForm<FormProps>
            schema={formSchema}
            onSubmit={onSubmit}
            defaultValues={{
                organizationName: defaultValueOrgName,
                country: defaultCountry,
                location: defaultValueLoc,
                zip: defaultValueLoc.zip,
            }}
            mode="onBlur"
        >
            <OrganizationAutoComplete />

            {newSearchUIEnabled && (
                <SuggestionsList
                    open={suggestionsOpen}
                    reOpen={() => setSuggestionsOpen(true)}
                    onClose={() => setSuggestionsOpen(false)}
                />
            )}
        </GivelifyForm>
    );
};

export default React.memo(Outer);
